import React from "react";
import { Calendar, RefreshCw, UserPlus, UserX, UserCheck, Trash2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format, addDays } from "date-fns";
import { BatchPicker } from "./BatchPicker";
import { PaymentHistory } from "./PaymentHistory";
import { StudentPickupSection } from "./StudentPickupSection";
import { StudentActivityLog } from "./StudentActivityLog";
import { CreatedByLine } from "@/components/shared/CreatedByLine";
import { ScrollableTabs } from "@/components/ui/scrollable-tabs";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { ContactPickerDialog } from "@/components/shared/ContactPickerDialog";
import {
  callPhone, openWhatsApp, buildContactOptions,
} from "@/lib/contactActions";
import { useBrand } from "@/lib/brand";

export const StudentEditDialog = ({
  open, onOpenChange,
  selectedStudent,
  editTab, setEditTab,
  // profile
  profileForm, setProfileForm,
  tshirtSizes = [],
  lookupsLoading = false,
  onUpdateProfile,
  onDiscontinue, onRejoin,
  // enrollments tab
  studentEnrollments,
  showRenewedEnrollments, setShowRenewedEnrollments,
  onOpenBatchChange,
  onOpenRenewal,
  onMarkNoRenewal,
  onUnmarkNoRenewal,
  onDeleteEnrollment,
  // enroll tab
  enrollForm,
  batches, styles, studios, instructors = [], masterInstructors = [], tenures,
  pricing = [],
  prorationPreview, previewLoading,
  activeCohort,
  // Feb 2026 — milestone-mode props (mirror of RenewalDialog)
  isMilestoneMode = false,
  onBatchSelect,
  onTenureChange,
  onStartDateChange,
  onChargeTypeChange,
  onDiscountTypeChange,
  onDiscountValueChange,
  onRenewalModeChange,
  onSessionCountChange,
  // payment
  paymentForm, setPaymentForm,
  onEnrollSubmit,
  paymentsRefreshKey = 0,
}) => {
  // Tenure dropdown filter: only show tenures that have a pricing row matching the
  // selected batch's style. When no batch is selected, list is empty + dropdown disabled.
  const selectedBatch = batches.find((b) => b.batch_id === enrollForm.batch_id);
  const selectedStyleId = selectedBatch?.style_id;
  const pricedTenures = React.useMemo(() => {
    if (!selectedStyleId) return [];
    const tenureIds = new Set(
      pricing
        .filter((p) => p.style_id === selectedStyleId)
        .map((p) => p.tenure_id)
    );
    return tenures.filter((t) => tenureIds.has(t.tenure_id));
  }, [selectedStyleId, pricing, tenures]);
  const priceFor = React.useCallback(
    (tenureId) => pricing.find((p) => p.style_id === selectedStyleId && p.tenure_id === tenureId),
    [pricing, selectedStyleId]
  );

  // ------------------------------------------------------------------
  // Membership status badge (Feb 2026): operational visibility moved from
  // the lightweight Students list into the profile header. Computes from
  // `studentEnrollments`:
  //   • No enrollments at all       → grey  "No Active Membership"
  //   • Has past enrollments, none active → red  "Membership Expired"
  //   • Active enrollment, end < now → red  "Membership Expired"
  //   • Active enrollment, end ≤ now+7d → amber "Renewal Due"
  //   • Else                          → green "Membership Active"
  // Discontinued students bypass this — the existing grey pill takes priority.
  // ------------------------------------------------------------------
  const membership = React.useMemo(() => {
    if (selectedStudent?.student_status === "discontinued") return null;
    const all = studentEnrollments || [];
    const active = all.filter(
      (e) => (e.status || "").toLowerCase() === "active"
    );
    if (all.length === 0) {
      // Never enrolled — neutral grey, distinct from "expired".
      return {
        kind: "none",
        label: "No Active Membership",
        className: "bg-slate-200 text-slate-700 border border-slate-300",
        sub: "Student has not been enrolled yet",
      };
    }
    if (active.length === 0) {
      // Had memberships before, all lapsed.
      return {
        kind: "expired",
        label: "Membership Expired",
        className: "bg-red-100 text-red-700 border border-red-200",
        sub: "No active enrollment",
      };
    }
    // Feb 2026 — "No Renewal Required" enrollments are excluded from
    // renewal computation. If EVERY active enrollment is exempt, show
    // a dedicated badge instead of "Renewal Due"/"Expired".
    const tracked = active.filter((e) => !e.renewal_exempt);
    if (tracked.length === 0) {
      return {
        kind: "no-renewal",
        label: "No Renewal Required",
        className: "bg-slate-100 text-slate-700 border border-slate-300",
        sub: "All active enrollments are renewal-exempt",
      };
    }
    const now = new Date();
    let earliest = null;
    for (const e of tracked) {
      if (!e.end_date) continue;
      const d = new Date(e.end_date);
      if (!earliest || d < earliest) earliest = d;
    }
    if (!earliest) {
      return {
        kind: "active",
        label: "Membership Active",
        className: "bg-emerald-100 text-emerald-700 border border-emerald-200",
        sub: null,
      };
    }
    const dueBy = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const dateStr = earliest.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    if (earliest < now) {
      return {
        kind: "expired",
        label: "Membership Expired",
        className: "bg-red-100 text-red-700 border border-red-200",
        sub: `Expired on ${dateStr}`,
      };
    }
    if (earliest <= dueBy) {
      return {
        kind: "due",
        label: "Renewal Due",
        className: "bg-amber-100 text-amber-700 border border-amber-200",
        sub: `Renews on ${dateStr}`,
      };
    }
    return {
      kind: "active",
      label: "Membership Active",
      className: "bg-emerald-100 text-emerald-700 border border-emerald-200",
      sub: `Renews on ${dateStr}`,
    };
  }, [studentEnrollments, selectedStudent?.student_status]);

  // ------------------------------------------------------------------
  // Quick-contact (Feb 2026 Feature 2): 📞 + WhatsApp icons next to the
  // membership badge. Always reads from the latest `selectedStudent` →
  // re-hydrated by the parent after every profile save (no stale phone).
  // ------------------------------------------------------------------
  const { default_country_code } = useBrand();
  const [contactPicker, setContactPicker] = React.useState(null); // {kind:"call"|"wa", options}

  const handleContactClick = (kind) => {
    const options = buildContactOptions({
      studentName: selectedStudent?.name,
      studentPhone: selectedStudent?.phone,
      guardianName: selectedStudent?.parent_name || "Guardian",
      guardianPhone: selectedStudent?.parent_phone,
    });
    if (options.length === 0) {
      // contactActions already toasts when this happens, but we still need
      // an early bail here so we don't open the dialog with zero options.
      const fire = kind === "call" ? callPhone : openWhatsApp;
      // Use student phone (likely empty) → triggers the standard error toast.
      fire(selectedStudent?.phone, { defaultCountryCode: default_country_code });
      return;
    }
    if (options.length === 1) {
      // Skip the picker — go straight to the only valid contact.
      const onlyKey = options[0].key;
      const phone = onlyKey === "student" ? selectedStudent?.phone : selectedStudent?.parent_phone;
      if (kind === "call") callPhone(phone, { defaultCountryCode: default_country_code });
      else openWhatsApp(phone, "Hi", { defaultCountryCode: default_country_code });
      return;
    }
    setContactPicker({ kind, options });
  };

  const handlePickerPick = (recipientKey) => {
    const kind = contactPicker?.kind;
    const phone = recipientKey === "student"
      ? selectedStudent?.phone
      : selectedStudent?.parent_phone;
    if (kind === "call") callPhone(phone, { defaultCountryCode: default_country_code });
    else if (kind === "wa") openWhatsApp(phone, "Hi", { defaultCountryCode: default_country_code });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-3 pr-8">
            {selectedStudent?.name}
            {selectedStudent?.student_status === "discontinued" && (
              <span className="px-3 py-1 bg-slate-200 text-slate-600 rounded-full text-sm font-medium">Discontinued</span>
            )}
          </DialogTitle>
          {membership && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <div
                data-testid={`membership-badge-${membership.kind}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold w-fit ${membership.className}`}
              >
                <span className={`w-2 h-2 rounded-full ${
                  membership.kind === "active" ? "bg-emerald-500" :
                  membership.kind === "due" ? "bg-amber-500" :
                  membership.kind === "expired" ? "bg-red-500" :
                  "bg-slate-400"
                }`} />
                <span>{membership.label}</span>
                {membership.sub && (
                  <span className="font-normal opacity-80">· {membership.sub}</span>
                )}
              </div>
              {/* Quick contact (Feb 2026 Feature 2) — phone + WhatsApp. */}
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleContactClick("call")}
                  data-testid="student-quick-call"
                  title="Call"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition border border-blue-200"
                >
                  <Phone className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleContactClick("wa")}
                  data-testid="student-quick-whatsapp"
                  title="WhatsApp"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition border border-emerald-200"
                >
                  <WhatsAppIcon size={16} />
                </button>
              </div>
            </div>
          )}
          <ScrollableTabs
            tabs={[
              { key: "profile", label: "Profile" },
              { key: "enrollments", label: "Classes" },
              { key: "enroll", label: "Enroll" },
              { key: "payments", label: "Payments" },
              { key: "pickup", label: "Pickup" },
              { key: "activity", label: "Activity" },
            ]}
            active={editTab}
            onChange={setEditTab}
          />
        </DialogHeader>

        {/* Profile Tab */}
        {editTab === "profile" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>Full Name</Label>
                <Input
                  data-testid="profile-name-input"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                  className="h-10 rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input
                  data-testid="profile-dob-input"
                  type="date"
                  value={profileForm.date_of_birth}
                  onChange={(e) => setProfileForm({...profileForm, date_of_birth: e.target.value})}
                  className="h-10 rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Age</Label>
                <Input
                  type="number"
                  value={profileForm.age}
                  onChange={(e) => setProfileForm({...profileForm, age: e.target.value})}
                  className="h-10 rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <select
                  value={profileForm.gender}
                  onChange={(e) => setProfileForm({...profileForm, gender: e.target.value})}
                  className="w-full h-10 rounded-lg border px-3 bg-white"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  data-testid="profile-phone-input"
                  value={profileForm.phone}
                  onChange={(e) => {
                    // Strip non-digits and cap at 10 — guard rails for the
                    // strict 10-digit numeric backend validator.
                    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
                    setProfileForm({...profileForm, phone: digits});
                  }}
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  className="h-10 rounded-lg"
                />
                {profileForm.phone && profileForm.phone.length !== 10 && (
                  <p data-testid="profile-phone-hint" className="text-xs text-red-600">
                    Mobile number must be exactly 10 digits
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                  className="h-10 rounded-lg"
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Address</Label>
                <Input
                  data-testid="profile-address-input"
                  value={profileForm.address}
                  onChange={(e) => setProfileForm({...profileForm, address: e.target.value})}
                  placeholder="Full address"
                  className="h-10 rounded-lg"
                />
              </div>
              {(() => {
                // Rule 2 — never render the dropdown into a partially-hydrated
                // state. While lookups load, show a disabled placeholder so the
                // user can't save thinking the field is "blank".
                if (lookupsLoading) {
                  return (
                    <div className="sm:col-span-2 space-y-2" data-testid="profile-tshirt-loading-block">
                      <Label>T-Shirt Size <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
                      <div className="w-full h-10 rounded-lg border border-slate-200 px-3 bg-slate-50 flex items-center text-slate-400 text-sm">
                        Loading sizes…
                      </div>
                    </div>
                  );
                }
                const activeSizes = (tshirtSizes || []).filter((s) => s.is_active);
                const selectedInactive = (tshirtSizes || []).find(
                  (s) => !s.is_active && s.size_id === profileForm.tshirt_size_id
                );
                if (activeSizes.length === 0 && !selectedInactive) return null;
                return (
                  <div className="sm:col-span-2 space-y-2">
                    <Label>T-Shirt Size <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
                    <select
                      data-testid="profile-tshirt-size"
                      value={profileForm.tshirt_size_id ?? ""}
                      onChange={(e) => setProfileForm({...profileForm, tshirt_size_id: e.target.value})}
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 bg-white"
                    >
                      <option value="">— Not specified —</option>
                      {activeSizes.map((s) => (
                        <option key={s.size_id} value={s.size_id}>{s.label}</option>
                      ))}
                      {selectedInactive && (
                        <option key={selectedInactive.size_id} value={selectedInactive.size_id}>
                          {selectedInactive.label} (Inactive)
                        </option>
                      )}
                    </select>
                  </div>
                );
              })()}
              <div className="space-y-2">
                <Label>Parent/Guardian Name</Label>
                <Input
                  value={profileForm.parent_name}
                  onChange={(e) => setProfileForm({...profileForm, parent_name: e.target.value})}
                  className="h-10 rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Parent/Guardian Phone</Label>
                <Input
                  data-testid="profile-parent-phone-input"
                  value={profileForm.parent_phone}
                  onChange={(e) => {
                    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
                    setProfileForm({...profileForm, parent_phone: digits});
                  }}
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  placeholder="10-digit mobile (optional)"
                  className="h-10 rounded-lg"
                />
                {profileForm.parent_phone && profileForm.parent_phone.length !== 10 && (
                  <p data-testid="profile-parent-phone-hint" className="text-xs text-red-600">
                    Parent phone must be exactly 10 digits
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={onUpdateProfile}
              data-testid="profile-update-button"
              disabled={
                lookupsLoading ||
                (profileForm.phone && profileForm.phone.length !== 10) ||
                (profileForm.parent_phone && profileForm.parent_phone.length !== 10)
              }
              title={lookupsLoading ? "Please wait — loading reference data…" : undefined}
              className="w-full h-12 rounded-full bg-primary disabled:opacity-60"
            >
              {lookupsLoading ? "Loading reference data…" : "Update Profile"}
            </Button>

            <div className="border-t border-slate-200 pt-4 mt-2">
              {selectedStudent?.student_status === "discontinued" ? (
                <Button
                  data-testid="rejoin-student-btn"
                  onClick={onRejoin}
                  className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <UserCheck className="w-5 h-5 mr-2" />
                  Rejoin Student
                </Button>
              ) : (
                <Button
                  data-testid="discontinue-student-btn"
                  onClick={onDiscontinue}
                  variant="outline"
                  className="w-full h-12 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                >
                  <UserX className="w-5 h-5 mr-2" />
                  Discontinue Student
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Enrollments Tab */}
        {editTab === "enrollments" && (
          <div className="space-y-4">
            {studentEnrollments.some(e => e.status === "renewed") && (
              <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showRenewedEnrollments}
                  onChange={(e) => setShowRenewedEnrollments(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Show renewed subscriptions
              </label>
            )}
            {studentEnrollments.filter(e => showRenewedEnrollments || e.status !== "renewed").length === 0 ? (
              <p className="text-center text-slate-600 py-8">No enrollments found</p>
            ) : (
              studentEnrollments.filter(e => showRenewedEnrollments || e.status !== "renewed").map((enrollment) => {
                const isRenewed = enrollment.status === "renewed";
                const isExempt = !isRenewed && !!enrollment.renewal_exempt;
                const isExpired = !isRenewed && !isExempt && new Date(enrollment.end_date) < new Date();
                const isExpiringSoon = !isExpired && !isRenewed && !isExempt && new Date(enrollment.end_date) <= addDays(new Date(), 7);

                return (
                  <div
                    key={enrollment.enrollment_id}
                    data-testid={`enrollment-row-${enrollment.enrollment_id}`}
                    className={`p-4 rounded-xl border ${
                      isRenewed ? "bg-slate-50 border-slate-200 opacity-70" :
                      isExempt ? "bg-slate-50 border-slate-300" :
                      isExpired ? "bg-red-50 border-red-200" :
                      isExpiringSoon ? "bg-orange-50 border-orange-200" :
                      "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {enrollment.batch?.name ? (
                            <h4 className="font-medium text-slate-900 truncate" data-testid="enrollment-batch-name">{enrollment.batch.name}</h4>
                          ) : (
                            <h4 className="font-medium text-amber-700 truncate flex items-center gap-1" data-testid="enrollment-batch-unavailable">
                              <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                              Batch unavailable
                            </h4>
                          )}
                          {isRenewed && <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-xs">Renewed</span>}
                          {isExempt && (
                            <span
                              className="px-2 py-0.5 bg-slate-200 text-slate-700 border border-slate-300 rounded-full text-xs font-medium"
                              data-testid={`enrollment-no-renewal-pill-${enrollment.enrollment_id}`}
                              title="This enrollment is marked as completed; renewal tracking is suppressed."
                            >
                              No Renewal Required
                            </span>
                          )}
                          {isExpired && <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs">Expired</span>}
                          {isExpiringSoon && <span className="px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full text-xs">Expiring Soon</span>}
                          {!enrollment.batch && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs" title="Linked batch record could not be found">
                              Reference broken
                            </span>
                          )}
                        </div>
                        {(enrollment.style?.name || enrollment.batch?.level) && (
                          <p className="text-sm text-slate-600 mt-1">
                            {[enrollment.style?.name, enrollment.batch?.level].filter(Boolean).join(" • ")}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(enrollment.start_date), "dd MMM")} - {format(new Date(enrollment.end_date), "dd MMM yyyy")}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-4 mt-2 flex-wrap">
                          {(enrollment.total_classes > 0 || enrollment.remaining_classes > 0) && (
                            <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-lg border border-blue-200">
                              {enrollment.remaining_classes ?? (enrollment.total_classes - (enrollment.consumed_classes || enrollment.classes_attended || 0))} / {enrollment.total_classes} classes left
                            </span>
                          )}
                          {enrollment.next_renewal_date && !isExempt && (
                            <span className="text-xs px-2 py-1 bg-violet-50 text-violet-700 rounded-lg border border-violet-200">
                              Renewal: {format(new Date(enrollment.next_renewal_date), "dd MMM yyyy")}
                            </span>
                          )}
                          {enrollment.price_per_class > 0 && (
                            <span className="text-xs text-slate-400">
                              ₹{enrollment.price_per_class}/class
                            </span>
                          )}
                        </div>
                      </div>
                      {!isRenewed && (
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onOpenBatchChange(enrollment)}
                          className="rounded-full text-xs flex-1 sm:flex-none"
                        >
                          Change Batch
                        </Button>
                        {(isExpired || isExpiringSoon) && !isExempt && (
                          <Button
                            size="sm"
                            onClick={() => onOpenRenewal(enrollment)}
                            className="rounded-full bg-green-500 hover:bg-green-600 text-xs flex-1 sm:flex-none"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Renew
                          </Button>
                        )}
                        {!isExempt && onMarkNoRenewal && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`mark-no-renewal-${enrollment.enrollment_id}`}
                            onClick={() => onMarkNoRenewal(enrollment)}
                            className="rounded-full text-xs flex-1 sm:flex-none text-slate-700 border-slate-300 hover:bg-slate-100"
                            title="Mark this enrollment as completed; suppress renewal tracking."
                          >
                            No Renewal Required
                          </Button>
                        )}
                        {isExempt && onUnmarkNoRenewal && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`resume-renewal-${enrollment.enrollment_id}`}
                            onClick={() => onUnmarkNoRenewal(enrollment)}
                            className="rounded-full text-xs flex-1 sm:flex-none text-violet-700 border-violet-300 hover:bg-violet-50"
                            title="Resume renewal tracking for this enrollment."
                          >
                            Resume Renewal Tracking
                          </Button>
                        )}
                        {onDeleteEnrollment && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`delete-enrollment-${enrollment.enrollment_id}`}
                            onClick={() => onDeleteEnrollment(enrollment)}
                            className="rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50"
                            title="Delete this enrollment (use only for duplicates)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      )}
                      <CreatedByLine
                        name={enrollment.created_by_name}
                        status={enrollment.created_by_status}
                        date={enrollment.created_at}
                        variant="compact"
                        testid={`enrollment-created-by-${enrollment.enrollment_id}`}
                        className="pt-1"
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* New Enrollment Tab */}
        {editTab === "enroll" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Batch</Label>
              <BatchPicker
                value={enrollForm.batch_id}
                batches={batches}
                styles={styles}
                studios={studios}
                instructors={instructors}
                masterInstructors={masterInstructors}
                testIdPrefix="enroll-batch"
                onChange={onBatchSelect}
              />
            </div>

            {activeCohort && (
              <div className="p-3 bg-violet-50 border border-violet-200 rounded-xl space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-violet-800">Active Cohort</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${activeCohort.status === "interest_capture" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                    {activeCohort.status.replace("_", " ").toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-violet-600">
                  {activeCohort.tenure_name} | {(activeCohort.completed_sessions || 0)}/{activeCohort.target_sessions} sessions done | ₹{activeCohort.fee}
                </p>

                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div className="bg-white/70 rounded-lg p-2">
                    <p className="text-[9px] text-violet-500 uppercase tracking-wide">Start</p>
                    <p className="text-xs font-semibold text-violet-900">
                      {activeCohort.start_date ? format(new Date(activeCohort.start_date), "dd MMM yy") : "—"}
                    </p>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2">
                    <p className="text-[9px] text-violet-500 uppercase tracking-wide">Min</p>
                    <p className="text-xs font-semibold text-violet-900">
                      {activeCohort.min_students ?? "—"} students
                    </p>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2">
                    <p className="text-[9px] text-violet-500 uppercase tracking-wide">Interest</p>
                    <p className="text-xs font-semibold">
                      <span className={(activeCohort.interested ?? 0) >= (activeCohort.min_students ?? 0) ? "text-green-700" : "text-amber-700"}>
                        {activeCohort.interested ?? 0}
                      </span>
                      <span className="text-violet-400"> / {activeCohort.min_students ?? "—"}</span>
                    </p>
                  </div>
                </div>

                {activeCohort.status === "interest_capture" && (
                  <p className="text-[10px] text-amber-600 font-medium">Interest capture active — enrollment in prorated mode only</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Select Tenure</Label>
              <select
                data-testid="enroll-tenure-select"
                value={enrollForm.tenure_id}
                onChange={(e) => onTenureChange(e.target.value)}
                className="w-full h-12 rounded-xl border px-4 bg-white disabled:bg-slate-50 disabled:text-slate-400"
                disabled={!!activeCohort || !enrollForm.batch_id || (isMilestoneMode && enrollForm.renewal_mode === "session")}
              >
                <option value="">
                  {!enrollForm.batch_id
                    ? "Select a batch first"
                    : pricedTenures.length === 0
                    ? "No priced tenures for this style"
                    : "Choose Tenure"}
                </option>
                {pricedTenures.map((tenure) => {
                  const p = priceFor(tenure.tenure_id);
                  const amt = p && p.price !== undefined ? `₹${Number(p.price).toLocaleString("en-IN")}` : "";
                  return (
                    <option key={tenure.tenure_id} value={tenure.tenure_id}>
                      {tenure.name} ({tenure.duration_months} months){amt ? ` — ${amt}` : ""}
                    </option>
                  );
                })}
              </select>
              {activeCohort && <p className="text-[10px] text-slate-500">Tenure is locked to the active cohort</p>}
              {enrollForm.batch_id && pricedTenures.length === 0 && !activeCohort && (
                <p className="text-[10px] text-amber-600">
                  No pricing configured for this style. Set up pricing in Studio Settings → Pricing.
                </p>
              )}
            </div>

            {/* Feb 2026 — Milestone renewal mode toggle + session count input.
                Mirrors RenewalDialog. Only shown when there is no active cohort. */}
            {isMilestoneMode && enrollForm.batch_id && !activeCohort && (
              <>
                <div className="space-y-2" data-testid="enroll-mode-section">
                  <Label className="text-xs font-semibold">Enrollment Mode</Label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onRenewalModeChange?.("tenure")}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${enrollForm.renewal_mode === "tenure" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}
                      data-testid="enroll-mode-tenure-btn"
                      type="button"
                    >
                      Tenure-based
                    </button>
                    <button
                      onClick={() => onRenewalModeChange?.("session")}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${enrollForm.renewal_mode === "session" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}
                      data-testid="enroll-mode-session-btn"
                      type="button"
                    >
                      Session-based
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Tenure = milestone-anchored cycles.  Session = pay for exactly N classes.
                  </p>
                </div>

                {enrollForm.renewal_mode === "session" && (
                  <div className="space-y-2" data-testid="enroll-session-count-section">
                    <Label>Number of Sessions</Label>
                    <Input
                      data-testid="enroll-session-count-input"
                      type="number"
                      min="1"
                      value={enrollForm.session_count || ""}
                      onChange={(e) => onSessionCountChange?.(e.target.value)}
                      className="h-12 rounded-xl"
                      placeholder="e.g. 6"
                    />
                    <p className="text-xs text-slate-500">
                      Renewal date = date of the Nth scheduled class from the start date.
                    </p>
                  </div>
                )}
              </>
            )}

            {enrollForm.batch_id && (
              <div className="space-y-2">
                <Label>Join Date</Label>
                <Input
                  data-testid="enroll-start-date"
                  type="date"
                  value={enrollForm.start_date}
                  onChange={(e) => onStartDateChange(e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>
            )}

            {enrollForm.start_date && (!isMilestoneMode || enrollForm.renewal_mode === "tenure") && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Pricing Mode</Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => onChargeTypeChange("full")}
                    disabled={activeCohort?.status === "interest_capture"}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${enrollForm.charge_type === "full" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"} ${activeCohort?.status === "interest_capture" ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="charge-type-full"
                  >
                    Full Fee
                  </button>
                  <button
                    onClick={() => onChargeTypeChange("prorated")}
                    disabled={isMilestoneMode && prorationPreview && !prorationPreview.is_prorated_available}
                    title={isMilestoneMode && prorationPreview && !prorationPreview.is_prorated_available
                      ? "Prorated not applicable — start date lands on a milestone boundary"
                      : ""}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${enrollForm.charge_type === "prorated" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"} ${(isMilestoneMode && prorationPreview && !prorationPreview.is_prorated_available) ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="charge-type-prorated"
                  >
                    Prorated
                  </button>
                </div>
                {isMilestoneMode && prorationPreview && !prorationPreview.is_prorated_available && (
                  <p className="text-[10px] text-slate-500">Start date lands exactly on a milestone — no proration needed.</p>
                )}
                {!activeCohort && !isMilestoneMode && (
                  <p className="text-[10px] text-slate-500">
                    Full Fee: charge the entire tenure regardless of join date. Prorated: charge only for actual sessions remaining.
                  </p>
                )}
              </div>
            )}

            {enrollForm.start_date && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Discount (optional)</Label>
                <div className="flex gap-2">
                  <select
                    data-testid="discount-type-select"
                    value={enrollForm.discount_type}
                    onChange={(e) => onDiscountTypeChange(e.target.value)}
                    className="h-10 rounded-xl border px-3 bg-white text-sm flex-1"
                  >
                    <option value="">No Discount</option>
                    <option value="flat">Fixed Amount (₹)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                  {enrollForm.discount_type && (
                    <Input
                      data-testid="discount-value-input"
                      type="number"
                      min="0"
                      max={enrollForm.discount_type === "percentage" ? "100" : undefined}
                      placeholder={enrollForm.discount_type === "percentage" ? "%" : "₹"}
                      value={enrollForm.discount_value}
                      onChange={(e) => onDiscountValueChange(e.target.value)}
                      className="h-10 rounded-xl text-sm w-24"
                    />
                  )}
                </div>
              </div>
            )}

            {previewLoading && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center">
                <p className="text-sm text-slate-500">Calculating proration...</p>
              </div>
            )}

            {prorationPreview && !previewLoading && isMilestoneMode && prorationPreview.status === "success" && (
              <div data-testid="milestone-preview" className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">Milestone Preview</p>
                  <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                    {prorationPreview.renewal_mode === "session" ? "Session-based" : `${prorationPreview.tenure_months || "—"}-month tenure`}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-xs text-slate-500">Classes</p>
                    <p className="text-lg font-bold text-slate-900">
                      {prorationPreview.classes_delivered}
                      {prorationPreview.renewal_mode === "tenure" && prorationPreview.nominal_class_count !== prorationPreview.classes_delivered && (
                        <span className="text-xs text-slate-500"> / {prorationPreview.nominal_class_count}</span>
                      )}
                    </p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-xs text-slate-500">
                      {prorationPreview.renewal_mode === "session" ? "Per Session" : "Renewal Date"}
                    </p>
                    {prorationPreview.renewal_mode === "session" ? (
                      <p className="text-lg font-bold text-slate-900">₹{prorationPreview.per_session_rate}</p>
                    ) : (
                      <p className="text-sm font-medium text-slate-900">
                        {prorationPreview.renewal_date ? format(new Date(prorationPreview.renewal_date), "dd MMM yyyy") : "—"}
                      </p>
                    )}
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-xs text-slate-500">
                      {prorationPreview.renewal_mode === "session" ? "Ends On" : "Mode"}
                    </p>
                    {prorationPreview.renewal_mode === "session" ? (
                      <p className="text-sm font-medium text-slate-900">
                        {prorationPreview.renewal_date ? format(new Date(prorationPreview.renewal_date), "dd MMM yyyy") : "—"}
                      </p>
                    ) : (
                      <p className="text-sm font-medium text-slate-700">{prorationPreview.tenure_months}-month</p>
                    )}
                  </div>
                </div>

                {prorationPreview.capped_at_batch_end && prorationPreview.capped_session_count && (
                  <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-800">
                      <strong>Capped at {prorationPreview.capped_session_count} sessions.</strong> Only {prorationPreview.capped_session_count} scheduled classes remain before the batch ends. Extend the batch&apos;s end date if you need the full session count.
                    </p>
                  </div>
                )}

                {prorationPreview.renewal_mode === "tenure" && (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Full Fee</span>
                      <span className={enrollForm.is_prorated ? "line-through text-slate-400" : "font-semibold text-slate-900"}>
                        ₹{prorationPreview.full_fee}
                      </span>
                    </div>
                    {prorationPreview.is_prorated_available && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">
                          Prorated Fee ({prorationPreview.classes_delivered} of {prorationPreview.nominal_class_count} classes)
                        </span>
                        <span className={enrollForm.is_prorated ? "font-semibold text-slate-900" : "line-through text-slate-400"}>
                          ₹{prorationPreview.prorated_fee}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {(prorationPreview.discount_amount ?? 0) > 0 && (
                  <div className="flex justify-between text-sm text-green-700 bg-green-50 rounded-lg p-2">
                    <span>
                      Discount ({prorationPreview.discount_type === "percentage" ? `${prorationPreview.discount_value}%` : `₹${prorationPreview.discount_value}`})
                    </span>
                    <span className="font-semibold">−₹{prorationPreview.discount_amount}</span>
                  </div>
                )}

                <div className="border-t pt-2 flex items-center justify-between">
                  <p className="text-sm text-slate-600">Total Payable</p>
                  <p data-testid="enrollment-fee" className="text-2xl font-bold text-emerald-700">
                    ₹{
                      prorationPreview.renewal_mode === "session"
                        ? (prorationPreview.session_fee ?? prorationPreview.full_fee ?? prorationPreview.final_fee ?? 0)
                        : (enrollForm.is_prorated && prorationPreview.is_prorated_available
                            ? (prorationPreview.prorated_fee ?? 0)
                            : (prorationPreview.full_fee ?? 0))
                    }
                  </p>
                </div>
              </div>
            )}

            {prorationPreview && !previewLoading && !isMilestoneMode && (
              <div data-testid="proration-preview" className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">Fee Breakdown</p>
                  <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                    {prorationPreview.style_name} &bull; {prorationPreview.tenure_name}
                  </span>
                </div>

                {prorationPreview.warnings && prorationPreview.warnings.length > 0 && (
                  <div className="space-y-1">
                    {prorationPreview.warnings.map((w, i) => (
                      <div key={`warn-${i}-${typeof w === "string" ? w.slice(0, 20) : ""}`} className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-xs text-amber-700">{w}</p>
                      </div>
                    ))}
                  </div>
                )}

                {prorationPreview.no_classes_this_month && (
                  <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-700">No classes remaining this month. Enrollment starts from next month.</p>
                  </div>
                )}

                {prorationPreview.tenure_end_capped && (
                  <div className="p-2 bg-blue-50 rounded-lg border border-blue-200" data-testid="tenure-cap-notice">
                    <p className="text-xs text-blue-800">
                      <strong>Tenure capped at batch end date.</strong> The batch ends on{" "}
                      {prorationPreview.batch_end_date ? format(new Date(prorationPreview.batch_end_date), "dd MMM yyyy") : "the batch end date"}.
                      This student's enrollment will end with the batch, and the fee has been adjusted accordingly.
                    </p>
                  </div>
                )}

                {prorationPreview.cohort_id ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Sessions</p>
                        <p className="text-lg font-bold text-slate-900">{prorationPreview.remaining_sessions}/{prorationPreview.total_sessions}</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Full Fee</p>
                        <p className="text-lg font-bold text-slate-500">₹{prorationPreview.full_fee}</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Base Fee</p>
                        <p className="text-lg font-bold text-slate-900">₹{prorationPreview.base_fee}</p>
                      </div>
                    </div>

                    {prorationPreview.discount_amount > 0 && (
                      <div className="flex justify-between text-sm text-green-600 bg-green-50 rounded-lg p-2">
                        <span>Discount ({prorationPreview.discount_type === "percentage" ? `${prorationPreview.discount_value}%` : `₹${prorationPreview.discount_value}`})</span>
                        <span className="font-bold">-₹{prorationPreview.discount_amount}</span>
                      </div>
                    )}

                    <div className="border-t pt-2 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Total Payable</p>
                        <p data-testid="enrollment-fee" className="text-2xl font-bold text-emerald-700">₹{prorationPreview.total_payable}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Est. End</p>
                        <p className="text-sm font-medium text-slate-700">
                          {prorationPreview.estimated_end_date ? format(new Date(prorationPreview.estimated_end_date), "dd MMM yyyy") : "—"}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Total Classes</p>
                        <p className="text-lg font-bold text-slate-900">{prorationPreview.total_classes}</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Per Class</p>
                        <p className="text-lg font-bold text-slate-900">₹{prorationPreview.price_per_class}</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-slate-500">Renewal</p>
                        <p className="text-sm font-medium text-slate-700">
                          {prorationPreview.next_renewal_date ? format(new Date(prorationPreview.next_renewal_date), "dd MMM yyyy") : "—"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs font-medium text-slate-600">Monthly Breakdown:</p>
                      {Object.entries(prorationPreview.monthly_breakdown || {}).map(([month, count]) => (
                        <div key={month} className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">{month}</span>
                          <span className="font-medium text-slate-800">{count} classes</span>
                        </div>
                      ))}
                    </div>

                    {/* Pricing breakdown — always shown */}
                    <div className="rounded-xl bg-white border border-slate-200 divide-y divide-slate-100">
                      <div className="flex items-center justify-between p-2.5 text-sm">
                        <span className="text-slate-500">Original Fee (Full Tenure)</span>
                        <span className={`font-medium ${enrollForm.charge_type === "full" ? "text-slate-900" : "text-slate-400 line-through"}`}>
                          ₹{prorationPreview.original_fee ?? prorationPreview.rate_card_price}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-2.5 text-sm">
                        <span className="text-slate-500">Prorated Fee ({prorationPreview.total_classes} classes)</span>
                        <span className={`font-medium ${enrollForm.charge_type === "prorated" ? "text-slate-900" : "text-slate-400 line-through"}`}>
                          ₹{prorationPreview.prorated_fee ?? prorationPreview.enrollment_fee}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-2.5 bg-slate-50 text-sm">
                        <span className="text-slate-700 font-medium">Base Fee ({enrollForm.charge_type === "full" ? "Full" : "Prorated"})</span>
                        <span className="font-semibold text-slate-900">₹{prorationPreview.base_fee ?? prorationPreview.enrollment_fee}</span>
                      </div>
                      {(prorationPreview.discount_amount ?? 0) > 0 && (
                        <div className="flex items-center justify-between p-2.5 text-sm bg-green-50">
                          <span className="text-green-700">
                            Discount ({prorationPreview.discount_type === "percentage" ? `${prorationPreview.discount_value}%` : `₹${prorationPreview.discount_value}`})
                          </span>
                          <span className="font-semibold text-green-700">−₹{prorationPreview.discount_amount}</span>
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-2 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Final Payable</p>
                        <p data-testid="enrollment-fee" className="text-2xl font-bold text-emerald-700">
                          ₹{prorationPreview.final_fee ?? prorationPreview.enrollment_fee}
                        </p>
                      </div>
                      {enrollForm.charge_type === "full" && (prorationPreview.original_fee ?? 0) !== (prorationPreview.prorated_fee ?? 0) && (
                        <div className="text-right text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-md">
                          Charged full tenure — student joining mid-cycle
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {prorationPreview && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-slate-900 mb-3">Payment Details</h4>

              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="payment_status"
                      checked={paymentForm.payment_status === "completed"}
                      onChange={() => setPaymentForm({...paymentForm, payment_status: "completed", amount_paid: ""})}
                    />
                    <span>Full Payment</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="payment_status"
                      checked={paymentForm.payment_status === "partial"}
                      onChange={() => setPaymentForm({...paymentForm, payment_status: "partial"})}
                    />
                    <span>Partial Payment</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="payment_status"
                      checked={paymentForm.payment_status === "pending"}
                      onChange={() => setPaymentForm({...paymentForm, payment_status: "pending", amount_paid: ""})}
                    />
                    <span>Payment Pending</span>
                  </label>
                </div>

                {paymentForm.payment_status === "partial" && prorationPreview && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-2">
                    <div className="space-y-2">
                      <Label>Amount Paid (₹)</Label>
                      <Input
                        type="number"
                        value={paymentForm.amount_paid}
                        onChange={(e) => setPaymentForm({...paymentForm, amount_paid: e.target.value})}
                        placeholder="Enter amount paid"
                        max={prorationPreview.final_fee ?? prorationPreview.total_payable ?? prorationPreview.enrollment_fee}
                        className="h-10 rounded-lg"
                      />
                    </div>
                    {paymentForm.amount_paid && (
                      <p className="text-sm text-amber-700">
                        Balance: ₹{Math.max(0, (prorationPreview.final_fee ?? prorationPreview.total_payable ?? prorationPreview.enrollment_fee) - parseFloat(paymentForm.amount_paid || 0))} will be pending
                      </p>
                    )}
                  </div>
                )}

                {(paymentForm.payment_status === "completed" || paymentForm.payment_status === "partial") && (
                  <>
                    <div className="space-y-2">
                      <Label>Payment Method</Label>
                      <select
                        value={paymentForm.payment_method}
                        onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                        className="w-full h-10 rounded-lg border px-3 bg-white"
                      >
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>

                    {paymentForm.payment_method === "cheque" && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Bank Name</Label>
                          <Input
                            value={paymentForm.cheque_bank_name}
                            onChange={(e) => setPaymentForm({...paymentForm, cheque_bank_name: e.target.value})}
                            placeholder="Enter bank name"
                            className="h-10 rounded-lg"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Cheque Number</Label>
                          <Input
                            value={paymentForm.cheque_number}
                            onChange={(e) => setPaymentForm({...paymentForm, cheque_number: e.target.value})}
                            placeholder="Enter cheque number"
                            className="h-10 rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            )}

            <Button
              data-testid="enroll-submit-btn"
              onClick={onEnrollSubmit}
              disabled={
                !enrollForm.batch_id ||
                !enrollForm.start_date ||
                !prorationPreview ||
                (isMilestoneMode
                  ? (enrollForm.renewal_mode === "tenure"
                      ? !enrollForm.tenure_id
                      : !(enrollForm.session_count && parseInt(enrollForm.session_count) > 0))
                  : !enrollForm.tenure_id)
              }
              className="w-full h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Enroll Student
            </Button>
          </div>
        )}

        {/* Payments Tab */}
        {editTab === "payments" && (
          <PaymentHistory studentId={selectedStudent?.user_id} refreshKey={paymentsRefreshKey} />
        )}

        {/* Pickup Tab */}
        {editTab === "pickup" && (
          <StudentPickupSection student={selectedStudent} />
        )}

        {/* Activity Tab — attendance + pickup timeline (Feb 2026) */}
        {editTab === "activity" && (
          <StudentActivityLog studentId={selectedStudent?.user_id} />
        )}
      </DialogContent>
      <ContactPickerDialog
        open={!!contactPicker}
        onOpenChange={(o) => { if (!o) setContactPicker(null); }}
        title={contactPicker?.kind === "call" ? "Who would you like to call?" : "Who would you like to message?"}
        options={contactPicker?.options || []}
        onPick={handlePickerPick}
      />
    </Dialog>
  );
};

export default StudentEditDialog;
